import { api_configuration } from "../../common";
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;

export default class SubscriptionBuilder {
	private _parent: SubscriptionBuilder | undefined;
	constructor(public properties: api_cr.Tnode_subscription["attributes"], parent: SubscriptionBuilder | undefined) {
		this._parent = parent;
	}
	static create(): SubscriptionBuilder {
		return new SubscriptionBuilder({}, undefined);
	}
	text(text: string): this {
		this.properties[text] = this.property(["text", { "include reference": ["no", {}] }]);
		return this;
	}
	file(file: string): this {
		this.properties[file] = this.property("file");
		return this;
	}
	number(value: string): this {
		this.properties[value] = this.property("number");
		return this;
	}
	command(command: string): this {
		this.properties[command] = {
			type: "command",
		};
		return this;
	}
	collection_updates(value: string, include_graph_endpoints: "yes" | "no" = "no"): this {
		const collection = this.properties[value];
		if (collection === undefined) {
			this.properties[value] = this.property([
				"collection",
				{
					"subscribe on": ["updates", {}],
					"include graph endpoints": [include_graph_endpoints, {}],
				},
			]);
		}
		return this;
	}

	collection_entries(
		collection: string,
		key_filters: string[] = [],
		include_graph_endpoints: "yes" | "no" = "no"
	): SubscriptionBuilder {
		const collection_property = this.getProperty(collection, "collection");
		if (
			collection_property !== undefined &&
			collection_property.type[1].type[1]["subscribe on"] instanceof Array &&
			collection_property.type[1].type[1]["subscribe on"][0] === "entries"
		) {
			return new SubscriptionBuilder(
				collection_property.type[1].type[1]["subscribe on"][1].subscription.attributes,
				this
			);
		} else {
			const collection_property = this.property([
				"collection",
				{
					"subscribe on": [
						"entries",
						{
							"key filter":
								key_filters.length > 0
									? [
											"yes",
											{
												keys: key_filters.reduce<Record<string, Record<string, any>>>((o, k) => {
													o[k] = {};
													return o;
												}, {}),
											},
									  ]
									: "no",
							subscription: { attributes: {} },
						},
					],
					"include graph endpoints": [include_graph_endpoints, {}],
				},
			]);
			this.properties[collection] = collection_property;
			const entry_subscription = collection_property.type[1].type[1]["subscribe on"][1];
			return new SubscriptionBuilder(entry_subscription.subscription.attributes, this);
		}
	}

	constrained_text(text: string): this {
		this.properties[text] = this.property([
			"text",
			{
				"include reference": ["yes", {}],
			},
		]);
		return this;
	}
	state_group(state_group: string, state_group_node: api_app.Cstate_group): this {
		if (this.properties[state_group] === undefined) {
			this.properties[state_group] = this.property([
				"state group",
				{
					states: state_group_node.properties.states.map<api_cr.Tstates__state_group>(() => ({
						subscription: {
							attributes: {},
						},
					})),
				},
			]);
		}
		return this;
	}
	state(state_group: string, state: string, state_group_node: api_app.Cstate_group): SubscriptionBuilder {
		const state_group_property =
			this.getProperty(state_group, "state group") ||
			this.property([
				"state group",
				{
					states: state_group_node.properties.states.map(() => ({
						subscription: {
							attributes: {},
						},
					})),
				},
			]);
		this.properties[state_group] = state_group_property;
		return new SubscriptionBuilder(state_group_property.type[1].type[1].states[state].subscription.attributes, this);
	}
	group(group: string): SubscriptionBuilder {
		const group_property =
			this.getProperty(group, "group") ||
			this.property([
				"group",
				{
					subscription: {
						attributes: {},
					},
				},
			]);
		this.properties[group] = group_property;
		return new SubscriptionBuilder(group_property.type[1].type[1].subscription.attributes, this);
	}

	parent(): SubscriptionBuilder {
		if (this._parent === undefined) {
			throw new Error("Parent is undefined");
		}
		return this._parent;
	}

	remove(property: string): void {
		delete this.properties[property];
	}

	private property<T extends api_cr.Tproperty__type__attributes["type"]>(
		property: T
	): {
		type: [
			"property",
			{
				type: T;
			}
		];
	} {
		return {
			type: [
				"property",
				{
					type: property,
				},
			],
		};
	}

	private getProperty<T extends Extract<api_cr.Tproperty__type__attributes["type"], any[]>[0]>(
		name: string,
		property_type: T
	):
		| {
				type: [
					"property",
					{
						type: [T, Extract<api_cr.Tproperty__type__attributes["type"], [T, any]>[1]];
					}
				];
		  }
		| undefined {
		const attribute = this.properties[name];
		if (attribute === undefined) {
			return undefined;
		}
		if (attribute.type instanceof Array && attribute.type[0] === "property") {
			if (attribute.type[1].type instanceof Array && attribute.type[1].type[0] === property_type) {
				return attribute as unknown as any;
			} else {
				throw new Error(`expected a ${property_type} but got ${attribute.type[1].type}`);
			}
		} else {
			throw new Error("expected a property " + attribute.type);
		}
	}
}
